/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone-14';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import ajax from '@client/components/ajax';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import CropModal from './CropModal';
import { toImgSrc, toVideoSrc } from '../../pages/CustomProgramPage/helpers';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { Media } from '../../../generated/graphql';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { toast } from 'react-hot-toast';
import { SimplePlayer } from '../pages/influencer/videos/components/SimplePlayer';
import classNames from 'classnames';
import { maxFileSizeBytes } from '../common/videos/videos.constants';
import * as styles from './MediaUpload.css';

const MB = 1024 * 1024;

export default NewMediaUpload;

type MediaUploadProps = {
  id: string; // NOTE: needed for crop modal
  creatorId: number;
  value: Media | null;
  onChange: (m: Media | null) => void;
  accept: Array<'image/png' | 'image/jpeg' | 'video/mp4' | 'video/quicktime'>;
  crop?: boolean;
  aspectRatio?: [number, number];
  showTimeLeft?: boolean;
  width?: string;
  height?: string;
  children: React.ReactNode;
  className?: string;
};
export function NewMediaUpload({
  children,
  accept = [],
  showTimeLeft = false,
  crop = false,
  aspectRatio,
  className,
  ...props
}: MediaUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const uploadStart = useRef<null | number>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [state, api] = useMediaUpload({
    id: props.id,
    creatorId: props.creatorId,
    withCropping: crop,
    aspectRatio,
    onChange: (metadata: Media | null) => {
      props.onChange(metadata);
      if (metadata) {
        setIsUploading(false);
      }
    },
    onProgress: (progress) => {
      if (progress === 0) {
        uploadStart.current = Date.now();
      }
      setUploadProgress(Math.floor(progress * 100));
      if (progress !== 1) {
        setIsUploading(true);
      }
    },
  });

  const formatToExtensions = {
    'image/png': ['.png', '.PNG'],
    'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
    'video/mp4': ['.mp4', '.MP4'],
    'video/quicktime': ['.mov', '.MOV'],
  };
  const acceptedFormats = accept.reduce((o, k) => {
    o[k] = formatToExtensions[k] || [];
    return o;
  }, {} as Partial<typeof formatToExtensions>);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: api.onDrop,
    accept: acceptedFormats,
    maxSize: maxFileSizeBytes,
    multiple: false,
  });

  const now = Date.now();
  const uploadSpeed = isUploading
    ? uploadProgress / ((now - (uploadStart?.current || 0)) / 1000)
    : 0;
  const timeLeft = isUploading
    ? Math.ceil((100 - uploadProgress) / uploadSpeed)
    : 0;

  const displayProgress = isUploading && uploadProgress > 0;

  if (props.value) {
    return (
      <MediaViewer
        media={props.value}
        onDelete={() => props.onChange(null)}
        className={className}
      />
    );
  }
  const inputProps = getInputProps();
  if (!inputProps.value) inputProps.value = '';
  return (
    <div>
      <div
        className={classNames(styles.card, className)}
        style={{ position: 'relative' }}
        data-testid={`upload-${props.id}`}
      >
        <div {...getRootProps()}>
          <input
            {...inputProps}
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: '0px',
              top: '0px',
              opacity: 0,
              cursor: 'pointer',
            }}
          />
          <div
            style={{
              display: props.value ? 'none' : 'block',
              cursor: 'pointer',
            }}
          >
            {displayProgress ? (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                {uploadProgress !== 100 && (
                  <CircularProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                )}
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                  >
                    {uploadProgress === 100
                      ? `Processing...`
                      : `${uploadProgress}%`}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <div>{children}</div>
            )}
          </div>
        </div>
        {displayProgress && showTimeLeft && (
          <div className="text-center w-full">
            <Typography
              variant="caption"
              component="span"
              color="text.secondary"
            >
              {timeLeft}s remaining
            </Typography>
          </div>
        )}
      </div>
      <CropModal
        isOpen={state.cropModal}
        onCancel={api.onCropCancel}
        onSave={api.onCropSave}
        aspectRatio={aspectRatio ? aspectRatio[0] / aspectRatio[1] : undefined}
        value={(state.files[0] as any)?.preview}
        onChange={(value) => api.updateState({ croppedImageUrl: value })}
        disabled={!state.croppedImageUrl}
      />
    </div>
  );
}

type MediaInfoProps = {
  icon?: IconProp;
  title: string;
  subtitle: string;
  textClassName?: string;
};
export function MediaInfo({
  icon,
  title,
  subtitle,
  textClassName,
}: MediaInfoProps) {
  return (
    <div className="text-center">
      {icon !== undefined ? (
        <Icon icon={icon} color="#8A8E99" className="w-[44px] h-[50px]" />
      ) : null}
      <div className={textClassName ?? 'text-primary'}>{title}</div>
      <div
        className={
          textClassName ??
          'text-sm opacity-50 text-[#151E33] whitespace-pre-wrap'
        }
      >
        {subtitle}
      </div>
    </div>
  );
}

type MediaViewerProps = {
  media: Media;
  onDelete: () => void;
  className?: string;
};
function MediaViewer({ media, onDelete, className }: MediaViewerProps) {
  const Tag = media.video ? SimplePlayer : 'img';
  const src = media.video ? toVideoSrc(media) : toImgSrc(media);
  const handleOnClick = (e: any) => {
    e.stopPropagation();
  };

  return (
    <div className={classNames(className, 'relative')} onClick={handleOnClick}>
      <div id="media-preview" className="rounded-lg overflow-hidden">
        <Tag
          src={src}
          className={classNames('object-cover', className)}
          poster={toImgSrc(media)}
        />
      </div>
      <div
        className="absolute w-7 h-7 bg-white top-[-8px] right-[-5px] cursor-pointer rounded-full"
        style={{ border: '1px solid #EAECF1' }}
        onClick={onDelete}
      >
        <Icon
          icon={faClose}
          color="#151E33"
          className="w-full h-full opacity-50 hover:text-red-500 hover:opacity-100"
        />
      </div>
    </div>
  );
}

function useMediaUpload(props: {
  aspectRatio?: [number, number];
  id: string;
  creatorId: number;
  onChange: (metadata: Media | null) => void;
  onProgress?: (progress: number) => void;
  withCropping?: boolean;
}) {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};
  const INITIAL_STATE = {
    files: [] as File[],
    preview: null,
    message: 'Drop or',
    inProgress: false,
    cropModal: false,
    croppedImageUrl: null as string | null,
  };
  const [state, setState] = useState(INITIAL_STATE);

  function updateState(newState: Partial<typeof state>) {
    setState((state) => ({ ...state, ...newState }));
  }

  const { aspectRatio } = props;

  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => {
    fileRejections.forEach((file) => {
      file.errors.forEach((err) => {
        console.error('fileRejection', err);
        if (err.code === 'file-too-large') {
          toast.error(`File is bigger than ${maxFileSizeBytes / MB}MB`, {
            duration: 5000,
            position: 'top-center',
          });
        }

        if (err.code === 'file-invalid-type') {
          toast.error(`Error: ${err.message}`, {
            duration: 5000,
            position: 'top-center',
          });
        }
      });
    });

    const files = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      }),
    );

    const file = files[0];

    if (!file) {
      return;
    }

    if (aspectRatio && !props.withCropping) {
      try {
        const requiredAspect = calculateAspectRatio(
          aspectRatio[0],
          aspectRatio[1],
        );
        const { aspectWidth, aspectHeight } = await getDimensions(file);
        if (
          aspectWidth !== requiredAspect[0] ||
          aspectHeight !== requiredAspect[1]
        ) {
          toast.error(
            `Media doesn't match aspect ratio (${aspectRatio[0]}:${aspectRatio[1]})`,
            {
              duration: 5000,
              position: 'top-center',
            },
          );
          return;
        }
      } catch (e: any) {
        return;
      }
    }

    updateState({ files, message: 'Uploading...', inProgress: true });
    if (props.withCropping) {
      updateState({ cropModal: true });
      return;
    }

    onDropContinue(files);
  };

  const onCropCancel = () => {
    updateState({ ...INITIAL_STATE });
  };

  const onCropSave = async () => {
    const croppedImageUrl = state.croppedImageUrl;

    if (!croppedImageUrl) {
      return;
    }
    updateState({ cropModal: false });

    const newBlob = await fetch(croppedImageUrl).then((r) => r.blob());
    const oldFile = state.files[0];

    onDropContinue([new File([newBlob], oldFile.name, oldFile)]);
  };

  const onDropContinue = (files: File[]) => {
    props.onChange(null);
    if (props.onProgress) {
      props.onProgress(0);
    }
    const isVideo = files[0].type.startsWith('video');
    ajax
      .upload(files[0], props.creatorId, {
        skipCompression: isVideo,
        onProgress: props.onProgress,
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((metadata: any) => {
        console.log('[MediaUpload] uploaded file', metadata);
        updateState({
          files: files,
          message: 'Drop or',
          inProgress: false,
        });
        props.onChange(metadata);
      })
      .catch((e) => {
        const status = e?.response?.status ?? e?.status;
        const errorMessage =
          typeof e?.response?.data === 'string'
            ? e.response.data
            : e?.message ?? 'Unknown error';

        updateState({
          files: [],
          message: 'Woops, we encountered an error. Please drop again or',
          inProgress: false,
        });

        if (status === 413) {
          toast.error('File too large on the server', {
            position: 'top-center',
            duration: 5000,
          });
          return;
        }
        if (status === 400) {
          toast.error(`Error when uploading: ${errorMessage}`, {
            position: 'top-center',
            duration: 5000,
          });
          return;
        }
        if (status === 502 || status === 503 || status === 504) {
          toast.error(
            'Upload server is temporarily unavailable. Please try again.',
            { position: 'top-center', duration: 5000 },
          );
          return;
        }
        console.error('[MediaEditor] Error uploading file.', e);
        toast.error(`Server error when uploading: ${errorMessage}`, {
          position: 'top-center',
          duration: 5000,
        });
      });
  };

  return [state, { onDrop, onCropCancel, onCropSave, updateState }] as const;
}

function getDimensions(file: File): Promise<{
  width: number;
  height: number;
  aspectRatio: number;
  aspectWidth: number;
  aspectHeight: number;
}> {
  return new Promise((resolve, reject) => {
    const dimensions = {
      width: 0,
      height: 0,
      aspectRatio: 0,
      aspectWidth: 0,
      aspectHeight: 0,
    };
    // add an event listener to the reader object to listen for when the file is loaded
    const isVideo = file.type.startsWith('video');

    const vidOrImg = document.createElement(isVideo ? 'video' : 'img');

    const widthProp = file.type.startsWith('video') ? 'videoWidth' : 'width';
    const heightProp = file.type.startsWith('video') ? 'videoHeight' : 'height';

    function resolveDimensions() {
      dimensions.width = (vidOrImg as any)[widthProp];
      dimensions.height = (vidOrImg as any)[heightProp];
      dimensions.aspectRatio = dimensions.width / dimensions.height;
      const divBy = gcd(dimensions.width, dimensions.height);
      dimensions.aspectHeight = dimensions.height / divBy;
      dimensions.aspectWidth = dimensions.width / divBy;
      resolve(dimensions);
    }
    (vidOrImg as HTMLVideoElement).preload = 'metadata';
    // add an event listener to the video object to listen for when the metadata is loaded
    vidOrImg.addEventListener('loadedmetadata', resolveDimensions);

    vidOrImg.addEventListener('error', function (e: any) {
      toast.error('Unknown error when loading media: ' + e.message, {
        duration: 5000,
        position: 'top-center',
      });
      reject(e);
    });

    // set the src attribute of the video object to the data URL of the selected file
    vidOrImg.src = (file as any).preview;

    if (!isVideo) {
      resolveDimensions(); // NOTE: not calling loadedmetada for images
    }
  });
}

function gcd(a: number, b: number) {
  if (a == 0) return b;
  return gcd(b % a, a);
}

function calculateAspectRatio(a: number, b: number) {
  const divBy = gcd(a, b);
  return [a / divBy, b / divBy];
}
